import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { HexColorPicker } from 'react-colorful';
import { useTranslation } from 'react-i18next';

/**
 * ColorPicker Component
 * A color picker with preview button, hex input, and popover color selector
 */
export const ColorPicker = ({ value, onChange, label, className = '', imageUrl = null }) => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '#000000');
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);
  const canvasRef = useRef(null);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value || '#000000');
  }, [value]);

  // Handle click outside to close popover and keyboard navigation
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    // FIX HIGH-003: Add keyboard support for escape key
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus(); // Return focus to color preview button
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen]);

  // Handle hex input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Validate hex color format
    if (/^#[0-9A-Fa-f]{6}$/.test(newValue)) {
      onChange(newValue);
    }
  };

  // Handle color picker change
  const handlePickerChange = (color) => {
    setInputValue(color);
    onChange(color);
  };

  // Handle input blur - validate and correct format
  const handleInputBlur = () => {
    let correctedValue = inputValue;

    // Add # if missing
    if (!correctedValue.startsWith('#')) {
      correctedValue = '#' + correctedValue;
    }

    // Validate and fallback to previous value if invalid
    if (!/^#[0-9A-Fa-f]{6}$/.test(correctedValue)) {
      correctedValue = value || '#000000';
    }

    setInputValue(correctedValue);
    if (correctedValue !== value) {
      onChange(correctedValue);
    }
  };

  /**
   * Handle eyedropper - pick color from image
   */
  const handleEyedropper = async () => {
    // Try using native EyeDropper API first (Chrome 95+)
    if ('EyeDropper' in window) {
      try {
        const eyeDropper = new window.EyeDropper();
        const result = await eyeDropper.open();
        if (result?.sRGBHex) {
          handlePickerChange(result.sRGBHex);
          setIsOpen(false);
        }
      } catch (error) {
        // User cancelled or error occurred
        console.log('EyeDropper cancelled:', error);
      }
    } else if (imageUrl) {
      // Fallback: Use canvas to pick color from provided image
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.crossOrigin = 'anonymous';
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          // Create a modal overlay with the image
          const modal = document.createElement('div');
          modal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.8);
            z-index: 9999;
            cursor: crosshair;
            display: flex;
            align-items: center;
            justify-content: center;
          `;

          const modalImg = document.createElement('img');
          modalImg.src = img.src;
          modalImg.style.cssText = `
            max-width: 90vw;
            max-height: 90vh;
            object-fit: contain;
          `;

          modal.appendChild(modalImg);

          const cleanup = () => {
            // Remove event listeners
            modal.removeEventListener('click', handleClick);
            document.removeEventListener('keydown', handleEscape);

            // Remove modal from DOM
            if (modal.parentNode) {
              document.body.removeChild(modal);
            }

            // Clear canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Release image resources
            img.onload = null;
            img.onerror = null;
            img.src = '';
            modalImg.src = '';
          };

          const handleClick = (e) => {
            const rect = modalImg.getBoundingClientRect();
            const scaleX = img.width / rect.width;
            const scaleY = img.height / rect.height;
            const x = Math.floor((e.clientX - rect.left) * scaleX);
            const y = Math.floor((e.clientY - rect.top) * scaleY);

            const pixel = ctx.getImageData(x, y, 1, 1).data;
            const hex = '#' + [pixel[0], pixel[1], pixel[2]]
              .map(x => x.toString(16).padStart(2, '0'))
              .join('');

            handlePickerChange(hex);
            cleanup();
            setIsOpen(false);
          };

          const handleEscape = (e) => {
            if (e.key === 'Escape') {
              cleanup();
            }
          };

          modal.addEventListener('click', handleClick);
          document.addEventListener('keydown', handleEscape);
          document.body.appendChild(modal);
        };

        img.onerror = (error) => {
          console.error('Failed to load image for eyedropper:', error);
          // CLEANUP: Release image resources on error
          img.onload = null;
          img.onerror = null;
          img.src = '';
        };

        img.src = imageUrl;
      } catch (error) {
        console.error('Failed to pick color from image:', error);
      }
    }
  };

  return (
    <div className={`color-picker-container ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      <div className="flex items-center gap-2">
        {/* Color Preview Button */}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={t('colorPicker.ariaLabels.colorPicker')}
          className="relative w-10 h-10 rounded-md border-2 border-gray-300 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          style={{ backgroundColor: value || '#000000' }}
        >
          <span className="sr-only">{t('colorPicker.ariaLabels.chooseColor')}</span>
        </button>

        {/* Hex Input Field */}
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono uppercase"
          placeholder="#000000"
          maxLength={7}
        />
      </div>

      {/* Color Picker Popover */}
      {isOpen && (
        <div className="relative">
          <div
            ref={popoverRef}
            className="absolute z-50 mt-2 p-3 bg-white rounded-lg shadow-xl border border-gray-200"
            style={{ left: 0 }}
          >
            <HexColorPicker
              color={value || '#000000'}
              onChange={handlePickerChange}
            />

            {/* Eyedropper button (if imageUrl provided or native API available) */}
            {(imageUrl || 'EyeDropper' in window) && (
              <button
                type="button"
                onClick={handleEyedropper}
                className="mt-3 w-full px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                aria-label={t('colorPicker.ariaLabels.eyedropper')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                {t('colorPicker.buttons.eyedropper')}
              </button>
            )}

            {/* Quick color presets */}
            <div className="mt-3 grid grid-cols-8 gap-1">
              {[
                '#000000', '#FFFFFF', '#FF0000', '#00FF00',
                '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
                '#808080', '#C0C0C0', '#800000', '#008000',
                '#000080', '#808000', '#800080', '#008080'
              ].map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => {
                    handlePickerChange(color);
                    setIsOpen(false);
                  }}
                  className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                  style={{ backgroundColor: color }}
                  aria-label={t('colorPicker.ariaLabels.presetColor', { color })}
                  tabIndex={0}  // FIX HIGH-003: Ensure preset buttons are keyboard accessible
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ColorPicker.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  className: PropTypes.string,
  imageUrl: PropTypes.string
};

export default ColorPicker;